import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { recipes, type RecipeData } from '@/data/mockData';

export interface RecipesContext {
  bloating: string;
  energy: string;
  bowel: string;
  protocolDay: number;
}

export interface UseRecipesDataReturn {
  loading: boolean;
  context: RecipesContext;
  favorites: string[];
  toggleFavorite: (id: string) => void;
  getRecommendedRecipes: () => RecipeData[];
  getRecommendationTitle: () => string;
  getRecommendationSubtitle: () => string;
}

export const useRecipesData = (): UseRecipesDataReturn => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [context, setContext] = useState<RecipesContext>({
    bloating: '', energy: '', bowel: '', protocolDay: 1,
  });
  const [favorites, setFavorites] = useState<string[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      console.log("DEBUG RECIPES: currentUser state", user);
      if (!user || !user.id) {
        console.log("DEBUG RECIPES: No user or user.id, skipping fetch.");
        setLoading(false);
        return;
      }

      // Load favorites from localStorage
      const savedFavs = localStorage.getItem(`recipe_favs_${user.id}`);
      if (savedFavs) setFavorites(JSON.parse(savedFavs));

      // Fetch context data via specialized recipes proxy
      try {
        const res = await fetch('/api/recipes/recommendations');
        const data = await res.json();

        if (!res.ok) {
          console.error("Error fetching recipe recommendations:", data.error);
          setLoading(false);
          return;
        }

        const { checkin: tc, completionsCount: pCount } = data;
        setContext({
          bloating: tc?.bloating_level || '',
          energy: tc?.energy_level || '',
          bowel: tc?.poop_quality || '',
          protocolDay: Math.min(pCount + 1, 21),
        });
      } catch (err) {
        console.error("Error fetching recipe data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const toggleFavorite = (id: string) => {
    setFavorites(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      if (user) localStorage.setItem(`recipe_favs_${user.id}`, JSON.stringify(next));
      return next;
    });
  };

  const getRecommendedRecipes = (): RecipeData[] => {
    if (context.bloating === 'Alta' || context.bloating === 'Moderada') {
      return recipes.filter(r => r.benefitTags.includes('ligero') || r.benefitTags.includes('hinchazon')).slice(0, 3);
    }
    if (context.bowel === 'No fui al baño' || context.bowel === 'Fui con dificultad') {
      return recipes.filter(r => r.benefitTags.includes('fibra')).slice(0, 3);
    }
    if (context.energy === 'Baja') {
      return recipes.filter(r => r.benefitTags.includes('energia') || r.benefitTags.includes('saciedad')).slice(0, 3);
    }
    if (context.protocolDay >= 8 && context.protocolDay <= 14) {
      return recipes.filter(r => r.benefitTags.includes('fermentado')).slice(0, 3);
    }
    // Default: quick and easy
    return recipes.filter(r => r.benefitTags.includes('facil')).slice(0, 3);
  };

  const getRecommendationTitle = (): string => {
    if (context.bloating === 'Alta' || context.bloating === 'Moderada') return 'Opciones ligeras para hoy';
    if (context.bowel === 'No fui al baño' || context.bowel === 'Fui con dificultad') return 'Más fibra para tu digestión';
    if (context.energy === 'Baja') return 'Energía para tu mañana';
    if (context.protocolDay >= 8 && context.protocolDay <= 14) return 'Opciones fermentadas';
    return 'Para ti hoy';
  };

  const getRecommendationSubtitle = (): string => {
    if (context.bloating === 'Alta' || context.bloating === 'Moderada') return 'Recetas suaves que alivian la hinchazón';
    if (context.bowel === 'No fui al baño' || context.bowel === 'Fui con dificultad') return 'Recetas ricas en fibra para apoyar tu tránsito';
    if (context.energy === 'Baja') return 'Opciones nutritivas para recargar tu energía';
    if (context.protocolDay >= 8 && context.protocolDay <= 14) return 'Alimentos con probióticos naturales';
    return 'Ideas prácticas para tu rutina';
  };

  return { loading, context, favorites, toggleFavorite, getRecommendedRecipes, getRecommendationTitle, getRecommendationSubtitle };
};
